<template>
  <div class="has-header">
    <div class="header main" v-header-position-lock>
      <div class="align-left"></div>
      <div class="align-middle">
        <headerbuttonstack :state="headerButtons" />
      </div>
      <div class="align-right"></div>
    </div>

    <div class="main-container">
      <div class="sidebar-container">
        <h5 class="sidebar-header">Filter auswählen:</h5>

        <FilterCompaniesAndGroups
          class="filter-list"
          @filter-change="onFilterChange"
          :init-selected-filter="selectedFilter"
          :type="filterType"
          :key="filterType + '-filter-' + JSON.stringify(selectedFilter)"
        />
        <FilterCompaniesAndGroups
          v-if="filterType2"
          class="filter-list"
          @filter-change="onFilterChange"
          :init-selected-filter="selectedFilter"
          :type="filterType2"
          :key="filterType2 + '-filter-' + JSON.stringify(selectedFilter)"
        />
      </div>

      <div class="promo-container">
        <div
          style="
            font-size: 32px;
            text-align: left;
            color: var(--mp);
            border-bottom: 1px solid #ccc;
            padding: 5px;
            font-weight: 400;
            margin-bottom: 25px;
          "
        >
          Promotions
        </div>
        <div class="promo-container-center">
          <div v-if="this.loading">Lädt...</div>
          <template v-else-if="!this.selectedFilter">
            <h5>Wählen Sie eine CompanyGroup oder Company aus...</h5>
          </template>
          <template v-else>
            <!-- Files Tab Title -->
            <template v-if="tab === 'files'">
              <h5>
                Kampagnen für <b>{{ selectedFilterToName }}</b>
              </h5>
            </template>
            <!-- Settings Tab Title -->
            <template v-if="tab === 'settings'">
              <h5>
                Kampagnen-Einstellungen für <b>{{ selectedFilterToName }}</b>
              </h5>
            </template>
            <!-- New Promo Tab Title -->
            <template v-if="tab === 'createPromo'">
              <h5>
                Neue Kampagne unter <b>{{ selectedFilterToName }}</b> erstellen
              </h5>
            </template>

            <!-- Promo Selector for files and settings tab -->
            <template v-if="tab === 'files' || tab === 'settings'">
              <hr />
              <template v-if="this.promotions === null">
                Promotions werden geladen...
              </template>
              <template v-else-if="promotions.length === 0">
                <h5>Keine Promotions vorhanden</h5>
                <h6 v-if="hasPermission('create_promotions')">
                  Erstellen Sie eine neue Kampagne!
                </h6>
              </template>
              <template v-else>
                <h5>Wählen Sie eine Promotion aus:</h5>
                <ul class="promotion-select-list">
                  <li
                    v-for="promotion in this.promotions"
                    :key="promotion.id"
                    :class="{ active: selectedPromoId === promotion.id }"
                    @click="selectPromo(promotion.id)"
                  >
                    <span>{{ promotion.name }}</span>
                  </li>
                </ul>
              </template>
            </template>

            <!-- Files Tab -->
            <template v-if="tab === 'files' && selectedPromoId">
              <files-table
                :promotion-id="selectedPromoId"
                ref="filesTable"
                :can-edit="hasPermission('edit_files')"
                :can-delete="hasPermission('delete_files')"
              />

              <div class="upload-box-subscription-container">
                <div class="upload-box-container">
                  <template v-if="hasPermission('upload_files')">
                    <file-upload-box
                      @upload-success="onFileUploadSuccess"
                      :promotion-id="selectedPromoId"
                    />
                  </template>
                  <div v-else class="no-permission-alert">
                    Sie haben keine Berechtigung um Dateien hochzuladen
                  </div>
                </div>

                <div class="player-subscription-container">
                  <template
                    v-if="hasPermission('promotion_view_subscriptions')"
                  >
                    <div class="player-subscription-list">
                      <i
                        v-if="!getSubscribedPlayers"
                        class="fas fa-circle-notch fa-spin"
                      ></i>
                      <p v-else-if="getSubscribedPlayers.errorMessage">
                        Fehler: {{ getSubscribedPlayers.errorMessage }}
                      </p>
                      <p
                        v-else-if="getSubscribedPlayers.companies.length === 0"
                      >
                        Diese Kampagne wird noch auf keinem Player angezeigt
                      </p>
                      <ul v-else>
                        <li class="subscription-list-header">
                          Diese Kampagne wird auf den folgenden Playern
                          angezeigt:
                        </li>
                        <template
                          v-for="company of getSubscribedPlayers.companies"
                        >
                          <li
                            :key="'sub-comp-' + company.id"
                            class="subscription-list-company"
                          >
                            {{ company.name }}
                          </li>

                          <template v-for="store of company.stores">
                            <li
                              :key="'sub-store-' + store.id"
                              class="subscription-list-store"
                            >
                              {{ store.name }}
                            </li>

                            <li
                              v-for="player of store.players"
                              :key="'sub-player-' + player.id"
                              class="subscription-list-player"
                            >
                              <span>{{ player.name }}</span>
                              <span
                                class="subscription-list-player-description"
                              >
                                {{ player.description }}
                              </span>
                            </li>
                          </template>
                        </template>
                      </ul>
                    </div>
                  </template>
                  <div v-else class="no-permission-alert">
                    Sie haben keine Berechtigung um Player, auf denen die
                    Kampagne angezeigt wird, einzusehen
                  </div>
                </div>
              </div>
            </template>

            <!-- Settings Tab -->
            <template v-if="tab === 'settings'">
              <EditPromotion
                :key="'edit-' + selectedPromoId"
                v-if="selectedPromoId !== null"
                :input-promotion="selectedPromotion"
                @changed="updatePromotion"
                @deleted="deletePromotion"
                :can-edit="this.hasPermission('edit_promotions')"
                :can-delete="this.hasPermission('delete_promotions')"
              ></EditPromotion>
            </template>

            <!-- New Promo Tab -->
            <template v-if="tab === 'createPromo'">
              <EditPromotion
                key="new"
                :input-promotion="null"
                @changed="createPromotion"
                :company-groups="companyGroups"
                :companies="companies"
                :selected-companies="
                  selectedFilter.type === 'company'
                    ? [selectedFilter.companyId]
                    : []
                "
                :selected-company-groups="
                  selectedFilter.type === 'companyGroup'
                    ? [selectedFilter.companyGroupId]
                    : []
                "
              ></EditPromotion>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FileUploadBox from "./FileUploadBox";
import FilesTable from "./FilesTable";
import EditPromotion from "./EditPromotion";
import FilterCompaniesAndGroups from "@/components/lists/FilterCompaniesAndGroups";
import { mapGetters } from "vuex";

export default {
  components: {
    FilterCompaniesAndGroups,
    FileUploadBox,
    FilesTable,
    EditPromotion,
  },
  name: "fileManagement",
  data() {
    return {
      promotions: [],
      selectedFilter: null,
      selectedPromoId: null,
      subscribedPlayers: {},
      loading: true,
      tab: "files",
    };
  },
  created: function () {
    //console.log("CREATED");
  },
  mounted: function () {
    if (this.$store.state.jwt !== undefined) {
      this.refresh();
    }
  },
  computed: {
    ...mapGetters(["companies", "companyGroups", "roles"]),
    authHeader() {
      return { Authorization: "Bearer " + this.$store.state.jwt };
    },
    headerButtons() {
      const hasCreatePermission = this.hasPermission("create_promotions");
      const hasEditPermission = this.hasPermission("edit_promotions");
      const hasDeletePermission = this.hasPermission("delete_promotions");
      return {
        buttons: [
          {
            type: "block",
            icon: "fa-file",
            click: () => {
              this.tab = "files";
            },
            tooltip: "Datei-Verwaltung",
            classes: this.tab === "files" ? "focus-highlight" : "",
          },
          {
            type: "block",
            icon: "fa-cogs",
            click: () => {
              this.tab = "settings";
            },
            tooltip: "Promo-Einstellungen",
            classes: this.tab === "settings" ? "focus-highlight" : "",
            disabled:
              !this.selectedFilter ||
              (!hasEditPermission && !hasDeletePermission),
          },
          {
            type: "block",
            icon: "fa-plus",
            click: () => {
              this.tab = "createPromo";
            },
            tooltip: hasCreatePermission
              ? "Neue Kampagne anlegen"
              : "Keine Berechtigung zum Erstellen neuer Kampagnen",
            classes: this.tab === "newPromo" ? "focus-highlight" : "green",
            disabled: !this.selectedFilter || !hasCreatePermission,
          },
        ],
      };
    },
    filterType() {
      return this.$store.state.isSuperUser
        ? "both"
        : this.companyGroups.length > 0
        ? "companyGroups"
        : "companies";
    },
    filterType2() {
      return !this.$store.state.isSuperUser &&
        this.companyGroups.length > 0 &&
        this.companies.length > 0
        ? "companies"
        : null;
    },
    selectedFilterToName() {
      if (this.selectedFilter.type === "company") {
        return this.companies.find(
          ({ id }) => id === this.selectedFilter.companyId
        ).name;
      } else {
        return this.companyGroups.find(
          ({ id }) => id === this.selectedFilter.companyGroupId
        ).name;
      }
    },
    selectedPromotion() {
      return this.selectedPromoId !== null
        ? (this.promotions || []).find(
            (promo) => promo.id === this.selectedPromoId
          )
        : null;
    },
    getSubscribedPlayers() {
      return this.subscribedPlayers[this.selectedPromoId];
    },
  },
  // methods that implement data logic.
  methods: {
    refresh: async function () {
      this.loading = true;
      await this.loadCompanies();
      await this.loadCompanyGroups();
      this.loading = false;
    },

    loadPromotions: async function (params = null) {
      if (!params && this.selectedFilter) {
        switch (this.selectedFilter.type) {
          case "company":
            params = { companyId: this.selectedFilter.companyId };
            break;
          case "companyGroup":
            params = { companyGroupId: this.selectedFilter.companyGroupId };
            break;
        }
      }
      const response = await this.axios.get("/api/sec/promotions", {
        headers: this.authHeader,
        params,
      });
      this.promotions = response.data;
    },

    loadCompanies: async function () {
      const response = await this.axios.get("/api/sec/list/companies", {
        headers: this.authHeader,
      });
      const companies = response.data.sort((compA, compB) => {
        const nameA = compA.name.toUpperCase();
        const nameB = compB.name.toUpperCase();
        return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
      });
      this.$store.commit("setCompanies", companies);
    },

    loadCompanyGroups: async function () {
      const response = await this.axios.get("/api/sec/list/companyGroups", {
        headers: this.authHeader,
      });
      this.$store.commit("setCompanyGroups", response.data);
    },

    loadSubscribedPlayers: async function (promotionId) {
      try {
        const response = await this.axios.get(
          `/api/sec/promotions/${promotionId}/subscribedPlayers`,
          {
            headers: this.authHeader,
          }
        );
        this.$set(this.subscribedPlayers, promotionId, response.data);
      } catch (e) {
        this.displayValidationError(e);
        this.$set(this.subscribedPlayers, promotionId, {
          errorMessage: e.response?.data?.message ?? "Unbekannter Fehler",
        });
      }
    },

    async onFilterChange(newFilter) {
      if (
        newFilter.companyGroupId === -1 &&
        newFilter.type === "companyGroup"
      ) {
        this.selectedFilter = null;
        return;
      }
      this.selectedFilter = newFilter;
      this.selectPromo(null);
      await this.loadPromotions();
    },

    selectPromo(promotionId) {
      this.selectedPromoId = promotionId;
      if (promotionId && this.hasPermission("promotion_view_subscriptions"))
        this.loadSubscribedPlayers(promotionId);
    },

    async createPromotion(promotion) {
      const {
        name,
        description,
        campaignTypeKeys,
        companyGroupIds,
        companyIds,
      } = promotion;
      try {
        const response = await this.$axios.post(
          "/api/sec/promotions",
          {
            name,
            description,
            campaignTypes: campaignTypeKeys,
            companyIds,
            companyGroupIds,
          },
          { headers: this.authHeader }
        );
        if (response.status < 400) {
          this.$bvToast.toast("Promotion erfolgreich erstellt!", {
            appendToast: true,
            autoHideDelay: 3000,
            toaster: "b-toaster-bottom-center",
            variant: "success",
          });
          this.tab = "files";
          await this.loadPromotions();
          this.selectedPromoId = response.data.id;
        } else {
          this.displayValidationError(
            new Error(
              `Ein unbekannter Fehler ist aufgetreten (HTTP-Status: ${response.status})`
            ),
            "Fehler beim Erstellen der Kampagne"
          );
        }
      } catch (error) {
        this.displayValidationError(
          error,
          "Fehler beim Erstellen der Kampagne"
        );
      }
    },
    async updatePromotion(promotion) {
      const {
        id,
        name,
        description,
        campaignTypeKeys,
        companyGroupIds,
        companyIds,
      } = promotion;
      try {
        const response = await this.$axios.put(
          `/api/sec/promotions/${id}`,
          {
            name,
            description,
            campaignTypes: campaignTypeKeys,
            companyIds,
            companyGroupIds,
          },
          { headers: this.authHeader }
        );
        if (response.status < 400) {
          this.$bvToast.toast("Promotion erfolgreich aktualisiert!", {
            appendToast: true,
            autoHideDelay: 3000,
            toaster: "b-toaster-bottom-center",
            variant: "success",
          });
        } else {
          this.displayValidationError(
            new Error(
              `Ein unbekannter Fehler ist aufgetreten (HTTP-Status: ${response.status})`
            ),
            "Fehler beim Speichern der Änderungen"
          );
        }
      } catch (error) {
        this.displayValidationError(
          error,
          "Fehler beim Speichern der Änderungen"
        );
      }
    },
    async deletePromotion(promotion) {
      const {
        id,
        name /*, description, campaignTypeKeys, companyGroupIds, companyIds*/,
      } = promotion;
      try {
        const response = await this.$axios.delete(`/api/sec/promotions/${id}`, {
          headers: this.authHeader,
        });
        if (response.status < 400) {
          this.$bvToast.toast(`Promotion "${name}" erfolgreich gelöscht!`, {
            appendToast: true,
            autoHideDelay: 3000,
            toaster: "b-toaster-bottom-center",
            variant: "success",
          });
          await this.loadPromotions();
          this.selectedPromoId = null;
        } else {
          this.displayValidationError(
            new Error(
              `Ein unbekannter Fehler ist aufgetreten (HTTP-Status: ${response.status})`
            ),
            "Fehler beim Löschen der Kampagne"
          );
        }
      } catch (error) {
        this.displayValidationError(error, "Fehler beim Löschen der Kampagne");
      }
    },

    onFileUploadSuccess(file) {
      // Trigger reload of files table
      this.$refs.filesTable.refresh();
    },

    displayValidationError(error, title) {
      // TODO: refactor this to a standalone helper method
      let message = error.message;

      if (error.response && error.response.data) {
        let valErrList = "";
        const errorData = error.response.data;
        if (errorData.errors) {
          const items = Object.entries(errorData.errors).map(([, valErr]) =>
            this.$createElement("li", [valErr.join(" ")])
          );
          valErrList = this.$createElement(
            "ul",
            { class: "error-list" },
            items
          );
        }
        message = this.$createElement("p", [errorData.message, valErrList]);
      }

      this.$bvToast.toast(message, {
        appendToast: true,
        autoHideDelay: 5000,
        toaster: "b-toaster-bottom-center",
        variant: "danger",
        title: title ?? "Fehler bei Serveranfrage",
      });
    },

    hasPermission(permission) {
      if (this.$store.state.isSuperUser) return true;

      if (!this.selectedFilter) return false;

      const forType = this.selectedFilter.type;
      const forId =
        forType === "company"
          ? this.selectedFilter.companyId
          : this.selectedFilter.companyGroupId;

      return !!this.roles.find((role) => {
        if (!role.role_data?.permissions?.includes(permission)) return false;
        const permCheckMap = {
          company: role.companyId === forId,
          companyGroup: role.companyGroupId === forId,
        };
        return permCheckMap[forType];
      });
    },
  },
};
</script>

<style scoped>
.main-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: stretch;
  height: 100%;
  color: var(--contrast-4);
}

.sidebar-container {
  flex: 2;
  max-width: 20rem;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-header {
  font-weight: bold;
  font-size: 1.25rem;
  padding: 0.5rem;
  text-align: left;
  background-color: var(--contrast-1);
  margin: 0;
}

.filter-list {
  flex: 100% 1 1;
  margin-bottom: 0;
}

.promo-container {
  flex: 6;
  padding: 1rem;
  height: 100%;
  overflow-y: scroll;
}

.promo-container-center {
  max-width: 1140px;
  margin-left: auto;
  margin-right: auto;
}

.promotion-select-list {
  list-style: none;
  max-height: 14.5rem;
  overflow-y: scroll;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
}

.promotion-select-list li {
  background-color: var(--contrast-1);
  /*flex: 50% 1 1;*/
  flex: 100% 1 1;
}

.promotion-select-list li.active {
  background-color: var(--contrast-3);
}

.promotion-select-list li:hover {
  background-color: var(--contrast-2);
}

.upload-box-subscription-container {
  display: flex;
  flex-direction: row;
  margin-top: 2rem;
}

.upload-box-container {
  flex: 6 6;
  margin-right: 1rem;
}

.player-subscription-container {
  flex: 2 2;
}

@media screen and (max-width: 1024px) {
  .upload-box-subscription-container {
    flex-direction: column;
    margin-top: 2rem;
  }

  .upload-box-container {
    margin-right: 0;
    margin-bottom: 1rem;
  }
}

.player-subscription-list {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--contrast-1);
  padding: 0.5rem 1rem;
}

.player-subscription-list > p {
  margin-bottom: 0;
}

.player-subscription-list > ul {
  list-style: none;
  text-align: left;
  flex: 100% 1 1;
  width: 100%;
  color: var(--contrast-4);
  margin: 0;
  overflow: auto;
  padding-bottom: 0.5rem;
}

.subscription-list-header {
  padding: 0.25rem 0;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid var(--contrast-8);
}

.subscription-list-company {
  padding-left: 0.5rem;
}

.subscription-list-store {
  padding-left: 1.5rem;
}

.subscription-list-player {
  padding-left: 2.5rem;
}

.subscription-list-player-description {
  margin-left: 0.5rem;
  color: var(--contrast-5);
}
</style>
<style>
.no-permission-alert {
  margin-top: 2rem;
  width: 100%;
  padding: 3rem 1rem;
  background: repeating-linear-gradient(
    45deg,
    var(--contrast-1),
    var(--contrast-1) 2rem,
    var(--contrast-2) 2rem,
    var(--contrast-2) 4rem
  );
  color: var(--contrast-4);
  text-align: center;
}
</style>
